/**
 * /pages/reviews — progressive enhancement of the server-rendered UGC wall.
 *
 * The tiles are already in the HTML (written into page.content by the daily
 * cron in analytics-portal → seo-fixer/reviews_page/build_reviews_page.py), so
 * Google and a JS-less browser get a working page: a grid of links to the
 * original Instagram posts. This script adds, on top of that markup:
 *
 *   · click → modal (photo/video, @author, caption, products, prev/next, swipe)
 *     — same behaviour and styling as the home-page Gridwave gallery;
 *   · infinite scroll from data-loaded onwards (offset paging, dedup by id);
 *   · view/click events for Gridwave analytics.
 *
 * Details the SSR markup does not carry (caption, video source, products) are
 * hydrated from the same widget API on first need — one request, cached.
 */
(function () {
  const grid = document.getElementById('elagia-reviews-grid');
  if (!grid || grid.dataset.rvInited) return;
  grid.dataset.rvInited = '1';

  const API = (grid.dataset.api || '').replace(/\/+$/, '');
  if (!API) return;
  const ORIGIN = (function () {
    try { return new URL(API).origin; } catch (e) { return ''; }
  })();
  const GRID = grid.dataset.grid || '';
  const SHOP = grid.dataset.shop || '';
  const PAGE_SIZE = Math.max(1, parseInt(grid.dataset.pageSize || '48', 10));

  let offset = parseInt(grid.dataset.loaded || '0', 10) || 0;
  let hasMore = grid.dataset.hasMore === 'true';
  let loading = false;
  let hydrated = false;

  /** DOM order == records order; the modal's prev/next walks this array. */
  const records = [];
  const byId = new Map();

  function adopt(tile) {
    const id = Number(tile.dataset.id);
    if (!id || byId.has(id)) return;
    const img = tile.querySelector('img');
    const rec = {
      id: id,
      mediaType: tile.dataset.type || 'IMAGE',
      permalink: tile.getAttribute('href') || '',
      thumbnailUrl: img ? img.getAttribute('src') : '',
      cachedUrl: '',
      videoCachedUrl: '',
      caption: '',
      authorName: (tile.querySelector('.rv-handle') || {}).textContent || '',
      source: 'tagged',
      takenAt: '',
      products: [],
    };
    rec.authorName = rec.authorName.replace(/^@/, '').trim();
    rec.index = records.push(rec) - 1;
    byId.set(id, rec);
    tile.dataset.rvIndex = String(rec.index);
  }

  Array.prototype.forEach.call(grid.querySelectorAll('.rv-tile'), adopt);
  if (!records.length) return;

  function merge(item) {
    const rec = byId.get(item.id);
    if (!rec) return null;
    rec.mediaType = item.mediaType || rec.mediaType;
    rec.permalink = item.permalink || rec.permalink;
    rec.cachedUrl = item.cachedUrl || item.mediaUrl || rec.cachedUrl;
    rec.videoCachedUrl = item.videoCachedUrl || '';
    rec.caption = item.caption || '';
    rec.authorName = item.authorName || rec.authorName;
    rec.source = item.source || rec.source;
    rec.takenAt = item.takenAt || item.syncedAt || '';
    rec.products = item.products || [];
    rec.hydrated = true;
    return rec;
  }

  function apiUrl(off) {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(off) });
    if (GRID) params.set('grid', GRID);
    return API + '?' + params.toString();
  }

  async function fetchPage(off) {
    const res = await fetch(apiUrl(off), { signal: AbortSignal.timeout(12000) });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const data = await res.json();
    return { items: Array.isArray(data.items) ? data.items : [], hasMore: !!data.hasMore };
  }

  /** Fill in caption / video / products for the tiles that came from SSR. */
  async function hydrate() {
    if (hydrated) return;
    hydrated = true;
    try {
      const page = await fetchPage(0);
      page.items.forEach(merge);
    } catch (e) {
      hydrated = false; // a failed hydration must not poison later clicks
      console.warn('[reviews] hydrate failed:', e);
    }
  }

  /* ── Tiles ─────────────────────────────────────────────────────────────── */
  function escapeAttr(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /** Gridwave stores product_price as a bare decimal string ("139.00") — add the currency. */
  function fmtPrice(value) {
    const s = String(value == null ? '' : value).trim();
    return /^\d+(\.\d+)?$/.test(s) ? '$' + s : s;
  }

  /** «JUL 24, 2026» — same stamp the Gridwave widget prints in its own modal. */
  function fmtDate(value) {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
  }

  function sourceLabel(source) {
    if (source === 'judgeme') return 'JUDGE.ME';
    if (source === 'telegram') return 'TELEGRAM';
    return 'INSTAGRAM';
  }

  function tileHtml(item) {
    const who = (item.authorName || '').replace(/^@/, '');
    const alt = who ? '@' + who + ' wearing ELAGIA' : 'Customer photo wearing ELAGIA';
    const src = item.thumbnailUrl || item.cachedUrl || item.mediaUrl || '';
    const href = item.permalink || (who ? 'https://www.instagram.com/' + who + '/' : '#');
    const play = item.mediaType === 'VIDEO' ? '<span class="rv-play" aria-hidden="true"></span>' : '';
    return (
      '<a class="rv-tile" href="' + escapeAttr(href) + '" target="_blank" rel="noopener nofollow ugc"' +
      ' data-id="' + escapeAttr(item.id) + '" data-type="' + escapeAttr(item.mediaType || 'IMAGE') + '"' +
      ' title="' + escapeAttr(alt) + '" aria-label="' + escapeAttr(alt) + '">' +
      '<img src="' + escapeAttr(src) + '" alt="' + escapeAttr(alt) + '" width="600" height="600"' +
      ' loading="lazy" decoding="async">' + play +
      '<span class="rv-handle">@' + escapeAttr(who) + '</span></a>'
    );
  }

  /* ── Infinite scroll ───────────────────────────────────────────────────── */
  const sentinel = document.createElement('div');
  sentinel.className = 'rv-sentinel';
  grid.appendChild(sentinel);

  async function loadMore() {
    if (loading || !hasMore) return;
    loading = true;
    try {
      const page = await fetchPage(offset);
      const fresh = page.items.filter((i) => i && !byId.has(i.id) && (i.thumbnailUrl || i.cachedUrl || i.mediaUrl));
      hasMore = page.hasMore && page.items.length > 0;
      offset += page.items.length;
      if (fresh.length) {
        const html = fresh.map(tileHtml).join('');
        sentinel.insertAdjacentHTML('beforebegin', html);
        const added = Array.prototype.slice.call(grid.querySelectorAll('.rv-tile:not([data-rv-index])'));
        added.forEach(adopt);
        fresh.forEach(merge);
      }
      if (!hasMore) {
        teardown();
      } else if (io) {
        // Sentinel не покидал вьюпорт → нового entry не будет и догрузка встанет
        // на второй странице. Пере-подписка заставляет observer выдать свежее
        // пересечение (ровно этим 48→96 и заканчивалось до фикса).
        io.unobserve(sentinel);
        io.observe(sentinel);
      }
    } catch (e) {
      console.warn('[reviews] load more failed:', e);
      hasMore = false;
      teardown();
    } finally {
      loading = false;
    }
  }

  let io = null;
  function teardown() {
    if (io) { io.disconnect(); io = null; }
    sentinel.remove();
  }

  if (hasMore && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) loadMore(); },
      { rootMargin: '600px' }
    );
    io.observe(sentinel);
  } else if (!hasMore) {
    sentinel.remove();
  }

  /* ── Modal ─────────────────────────────────────────────────────────────── */
  let modal = null;
  let modalIndex = 0;

  function buildModal() {
    const root = document.createElement('div');
    root.className = 'rvm';
    root.hidden = true;
    root.innerHTML =
      '<div class="rvm__backdrop" data-rvm-close></div>' +
      '<div class="rvm__dialog" role="dialog" aria-modal="true" aria-label="Customer post">' +
      '<button class="rvm__nav rvm__prev" type="button" aria-label="Previous">&#8249;</button>' +
      '<button class="rvm__nav rvm__next" type="button" aria-label="Next">&#8250;</button>' +
      '<button class="rvm__close" type="button" aria-label="Close" data-rvm-close>&times;</button>' +
      '<div class="rvm__media"></div>' +
      '<div class="rvm__side">' +
      // Owner's order: who/where/when → what to buy → what they wrote → link out.
      // The product card is the CTA, so it sits above the caption, not under it.
      '<div class="rvm__meta"></div>' +
      '<div class="rvm__shop" hidden><div class="rvm__shop-label">Shop this post</div>' +
      '<div class="rvm__products"></div></div>' +
      '<div class="rvm__caption"></div>' +
      '<a class="rvm__link" target="_blank" rel="noopener nofollow ugc">View on Instagram &#8599;</a>' +
      '</div></div>';

    document.body.appendChild(root);

    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-rvm-close]')) close();
    });
    root.querySelector('.rvm__prev').addEventListener('click', () => step(-1));
    root.querySelector('.rvm__next').addEventListener('click', () => step(1));
    document.addEventListener('keydown', (e) => {
      if (root.hidden) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    });

    let x0 = null;
    root.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    root.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
    }, { passive: true });

    return root;
  }

  function render() {
    const rec = records[modalIndex];
    if (!rec) return;
    const q = (sel) => modal.querySelector(sel);

    const poster = rec.thumbnailUrl || rec.cachedUrl || '';
    q('.rvm__media').innerHTML = rec.videoCachedUrl
      ? '<video src="' + escapeAttr(rec.videoCachedUrl) + '" poster="' + escapeAttr(poster) +
        '" controls autoplay playsinline></video>'
      : '<img src="' + escapeAttr(rec.cachedUrl || poster) + '" alt="' +
        escapeAttr((rec.authorName ? '@' + rec.authorName + ' ' : '') + 'wearing ELAGIA') + '">';

    const meta = q('.rvm__meta');
    meta.innerHTML = '';
    const bits = [];
    if (rec.authorName) bits.push('<span class="rvm__meta-who">@' + escapeAttr(rec.authorName) + '</span>');
    bits.push('<span class="rvm__meta-src">' + escapeAttr(sourceLabel(rec.source)) + '</span>');
    const stamp = fmtDate(rec.takenAt);
    if (stamp) bits.push('<span class="rvm__meta-date">' + escapeAttr(stamp) + '</span>');
    meta.innerHTML = bits.join('<span class="rvm__meta-sep">//</span>');

    const caption = (rec.caption || '').trim();
    const capEl = q('.rvm__caption');
    capEl.textContent = caption;
    capEl.hidden = !caption;

    const products = rec.products || [];
    q('.rvm__products').innerHTML = products.map((p) =>
      '<a class="rvm__product" href="' + escapeAttr(p.productUrl) + '">' +
      (p.productImage ? '<img src="' + escapeAttr(p.productImage) + '" alt="">' : '') +
      '<span class="rvm__product-i"><span class="rvm__product-t">' + escapeAttr(p.productTitle || '') + '</span>' +
      '<span class="rvm__product-p">' + escapeAttr(fmtPrice(p.productPrice)) + '</span></span></a>'
    ).join('');
    // No products → the whole block goes away, so the caption doesn't hang under a gap.
    q('.rvm__shop').hidden = products.length === 0;

    const link = q('.rvm__link');
    link.href = rec.permalink || '#';
    link.hidden = !rec.permalink;

    const many = records.length > 1;
    q('.rvm__prev').hidden = !many;
    q('.rvm__next').hidden = !many;
  }

  function step(dir) {
    if (!records.length) return;
    modalIndex = (modalIndex + dir + records.length) % records.length;
    render();
  }

  function close() {
    if (!modal || modal.hidden) return;
    const v = modal.querySelector('video');
    if (v) v.pause();
    modal.hidden = true;
    document.body.style.overflow = '';
  }

  async function open(index) {
    modalIndex = index;
    if (!modal) modal = buildModal();
    render();                     // instant: poster + handle from the SSR tile
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    modal.querySelector('.rvm__close').focus();
    if (!records[index].hydrated) {
      await hydrate();
      if (!modal.hidden) render(); // caption / video / products, once they arrive
    }
  }

  grid.addEventListener('click', (e) => {
    const tile = e.target.closest('.rv-tile');
    if (!tile || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    const index = parseInt(tile.dataset.rvIndex || '-1', 10);
    if (index < 0) return;
    sendEvent('click', Number(tile.dataset.id));
    open(index);
  });

  // Warm the details up while the visitor is still scrolling, so the first
  // click opens a complete modal instead of a poster.
  const warm = () => hydrate();
  if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 4000 });
  else setTimeout(warm, 2500);

  /* ── Analytics (fire-and-forget) ───────────────────────────────────────── */
  function sendEvent(eventType, mediaId) {
    if (!ORIGIN || !SHOP) return;
    try {
      fetch(ORIGIN + '/api/widget/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shopSlug: SHOP,
          gridSlug: GRID || undefined,
          eventType: eventType,
          mediaId: mediaId,
          pageUrl: location.href,
        }),
        keepalive: true,
        mode: 'cors',
      }).catch(() => {});
    } catch (e) {
      /* analytics is never a reason to break the gallery */
    }
  }

  if ('IntersectionObserver' in window) {
    const viewIo = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      viewIo.disconnect();
      sendEvent('view');
    }, { threshold: 0.2 });
    viewIo.observe(grid);
  } else {
    sendEvent('view');
  }
})();
